import { type Href } from 'expo-router';
import {
  ActivityIndicator,
  Pressable,
  Text as RNText,
  type PressableProps,
} from 'react-native';

import { cn } from './cn';
import { useFormSubmit } from './Form';
import { Linkable } from './Linkable';

export type ButtonVariant = 'primary' | 'ink' | 'outline' | 'chip' | 'ghost' | 'create'; // card chip with a signal "+" — for "New" / add actions
export type ButtonSize = 'sm' | 'md' | 'lg';

const SIZE: Record<ButtonSize, string> = {
  sm: 'px-3 py-1 rounded-[9px] gap-1.5',
  md: 'px-[18px] py-3 rounded-[11px] gap-2',
  lg: 'px-6 py-[15px] rounded-[14px] gap-2',
};

const CONTAINER: Record<ButtonVariant, string> = {
  primary: 'bg-signal',
  ink: 'bg-ink',
  outline: 'border-[1.5px] border-foreground bg-transparent',
  chip: 'bg-card border-[1.5px] border-border-strong rounded-[10px]',
  ghost: 'bg-transparent',
  create: 'bg-card border-[1.5px] border-border-strong rounded-xl',
};

const LABEL_COLOR: Record<ButtonVariant, string> = {
  primary: 'text-on-signal',
  ink: 'text-on-ink',
  outline: 'text-foreground',
  chip: 'text-foreground',
  ghost: 'text-muted',
  create: 'text-foreground',
};

// Variants that tint the leading icon with the signal accent instead of the label color.
const ICON_ACCENT: Partial<Record<ButtonVariant, string>> = {
  chip: 'text-signal text-lg',
  create: 'text-signal text-lg',
  ink: 'text-signal text-lg',
};

const LABEL_SIZE: Record<ButtonSize, string> = {
  sm: 'text-sm',
  md: 'text-sm',
  lg: 'text-[15px]',
};

const SPINNER_ACCENT: Record<ButtonVariant, string> = {
  primary: 'accent-on-signal',
  ink: 'accent-on-ink',
  outline: 'accent-foreground',
  chip: 'accent-foreground',
  ghost: 'accent-muted',
  create: 'accent-foreground',
};

export interface ButtonProps extends Omit<PressableProps, 'children' | 'style'> {
  label: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Optional leading glyph/emoji. */
  icon?: string;
  /** Optional trailing glyph/emoji (e.g. a "›" chevron). */
  iconRight?: string;
  /** Stretch to full width. */
  block?: boolean;
  /** Show a spinner and disable. */
  busy?: boolean;
  /** Act as the submit button for the enclosing <Form> (uses its onSubmit as onPress). */
  submit?: boolean;
  /** Navigation target — renders a real `<a href>` on web (cmd/middle-click, etc.). */
  href?: Href;
  className?: string;
}

export function Button({
  label,
  variant = 'primary',
  size = 'md',
  icon,
  iconRight,
  block,
  busy,
  disabled,
  submit,
  href,
  onPress,
  className,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || busy;
  const formSubmit = useFormSubmit();
  const handlePress = onPress ?? (submit ? () => formSubmit?.() : undefined);
  const labelFont = variant === 'ghost' ? 'font-sans-semibold' : 'font-heading';
  const labelClasses = cn(labelFont, LABEL_COLOR[variant], LABEL_SIZE[size]);

  return (
    <Linkable href={href} disabled={isDisabled}>
      <Pressable
        disabled={isDisabled}
        onPress={handlePress}
        accessibilityRole="button"
        className={cn(
          'flex-row items-center justify-center active:scale-[0.97]',
          SIZE[size],
          CONTAINER[variant],
          block && 'w-full',
          isDisabled && (variant === 'outline' ? 'border-faint' : 'opacity-40'),
          className,
        )}
        {...props}
      >
        {busy ? (
          <ActivityIndicator size="small" colorClassName={SPINNER_ACCENT[variant]} />
        ) : (
          <>
            {icon ? (
              <RNText className={cn(labelClasses, ICON_ACCENT[variant])}>{icon}</RNText>
            ) : null}
            <RNText className={labelClasses}>{label}</RNText>
            {iconRight ? (
              <RNText className={cn(labelClasses, ICON_ACCENT[variant])}>
                {iconRight}
              </RNText>
            ) : null}
          </>
        )}
      </Pressable>
    </Linkable>
  );
}
